import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NEAR_PCT = 0.5;
const RESET_PCT = 1.2;
const say = (n) => (n == null ? "" : Math.abs(n) >= 1000 ? Math.round(n).toLocaleString("en-US") : Number(n).toFixed(2));

// Derives spoken alerts from live dashboard state: S/D zones, Mag 7 bias, CHoCH reversals, entries/exits.
export function detectAlerts(state, mem) {
  const out = [];
  const push = (key, text, symbol, level = "info", price = null) => {
    if (mem.fired.has(key)) return;
    mem.fired.add(key); out.push({ id: `${key}:${Date.now()}`, key, text, symbol, level, price, ts: Date.now() / 1000 });
  };
  for (const m of state?.market || []) {
    const p = m.price; if (!p) continue;
    for (const z of m.zones || []) {
      const k = `${m.symbol}:${z.type}:${z.low}:${z.high}`;
      const inside = p >= z.low && p <= z.high;
      const edge = z.type === "supply" ? z.low : z.high;
      const dist = Math.abs(p - edge) / p * 100;
      const toward = z.type === "supply" ? p < z.low : p > z.high;
      if (inside) push(`${k}:in`, `${m.symbol} entering ${z.type} zone, ${say(z.low)} to ${say(z.high)}.`, m.symbol, z.type === "supply" ? "warn" : "good", edge);
      else if (toward && dist <= NEAR_PCT) push(`${k}:near`, `${m.symbol} approaching ${z.type} at ${say(edge)}.`, m.symbol, "info", edge);
      if (!inside && dist > RESET_PCT) { mem.fired.delete(`${k}:in`); mem.fired.delete(`${k}:near`); }
    }
    const st = m.structure;
    if (st?.event === "CHoCH" && st.level != null) push(`${m.symbol}:choch:${st.direction}:${st.level}`, `Possible reversal on ${m.symbol}. Change of character, ${String(st.direction).toLowerCase()}, at ${say(st.level)}.`, m.symbol, "alert", st.level);
  }
  const bias = state?.mag7?.bias;
  if (bias && mem.mag7 !== undefined && bias !== mem.mag7) {
    const g = state.mag7;
    push(`mag7:${bias}:${Date.now()}`, `Mag seven bias now ${bias.toLowerCase()}. ${g.up} up, ${g.down} down.`, null, bias === "BULLISH" ? "good" : bias === "BEARISH" ? "alert" : "warn");
  }
  if (bias) mem.mag7 = bias;
  const now = new Map((state?.positions || []).map((p) => [p.id, p]));
  if (mem.pos) {
    for (const [id, p] of now) if (!mem.pos.has(id)) push(`entry:${id}`, `Entered ${String(p.direction || "").toLowerCase()} ${p.root || p.symbol}, ${p.qty || 1} contract${(p.qty || 1) > 1 ? "s" : ""} at ${say(p.entry_price)}.`, p.root || p.symbol, "good", p.entry_price);
    for (const [id, p] of mem.pos) if (!now.has(id)) push(`exit:${id}`, `Exited ${p.root || p.symbol}. ${p.pnl != null ? `P and L ${p.pnl >= 0 ? "plus" : "minus"} ${Math.abs(Math.round(p.pnl))} dollars.` : ""}`, p.root || p.symbol, (p.pnl ?? 0) >= 0 ? "good" : "alert");
  }
  mem.pos = now;
  return out;
}

export function useVoiceAlerts(state) {
  const [on, setOn] = useState(() => localStorage.getItem("ripp_voice") !== "off");
  const [log, setLog] = useState([]);
  const [blocked, setBlocked] = useState(false);
  const mem = useRef({ fired: new Set(), mag7: undefined, pos: null, quietUntil: Date.now() + 10000 });
  const queue = useRef([]); const busy = useRef(false);
  useEffect(() => { localStorage.setItem("ripp_voice", on ? "on" : "off"); }, [on]);

  const fallback = (text) => new Promise((res) => {
    if (!window.speechSynthesis) return res();
    const u = new SpeechSynthesisUtterance(text); u.rate = 1.05; u.onend = res; u.onerror = res; window.speechSynthesis.speak(u);
  });
  const play = async (text) => {
    try {
      const r = await axios.post(`${API}/voice-alerts/speak`, { text }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = new Audio(url);
      await new Promise((res, rej) => { a.onended = res; a.onerror = rej; a.play().then(() => setBlocked(false)).catch(rej); });
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e?.name === "NotAllowedError") { setBlocked(true); return; }
      await fallback(text);
    }
  };
  const drain = async () => {
    if (busy.current) return; busy.current = true;
    while (queue.current.length) await play(queue.current.shift());
    busy.current = false;
  };
  useEffect(() => {
    if (!state) return;
    const found = detectAlerts(state, mem.current);
    if (Date.now() < mem.current.quietUntil) return;                    // first 10 s: learn the current state silently
    if (!found.length) return;
    setLog((l) => [...found.reverse(), ...l].slice(0, 30));
    if (on) { queue.current.push(...found.map((f) => f.text).slice(0, 3)); queue.current = queue.current.slice(-4); drain(); }
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps
  const test = () => { setBlocked(false); queue.current.push("RIPP voice alerts are on."); drain(); };
  return { on, setOn, log, blocked, test };
}
